import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import ScoringChecklist from './ScoringChecklist';
import Leaderboard from './Leaderboard';
import '../App.css';

// DESIGNERS will be loaded dynamically from the game
// Requirements and client changes are now loaded dynamically from the database

function ReviewPage({ currentGameId }) {
  const { isHost } = useAuth();
  const [selectedDesigner, setSelectedDesigner] = useState('All'); // 'All' or specific designer name
  const [selectedSession, setSelectedSession] = useState(null); // Only used when a specific designer is selected
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]); // Store session info with display names
  const [sessionRequirements, setSessionRequirements] = useState({}); // { sessionKey: [requirements] }
  const [clientChanges, setClientChanges] = useState({}); // { sessionKey: [changes] }
  const [designers, setDesigners] = useState([]); // Load designers from game
  const [fullscreenImage, setFullscreenImage] = useState(null); // Track which image to show fullscreen
  const [scoringStatus, setScoringStatus] = useState({}); // { sessionKey: boolean } - tracks which sessions have scores locked

  // Handle ESC key to close fullscreen image
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && fullscreenImage) {
        setFullscreenImage(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [fullscreenImage]);

  // Load submissions with scores - wrapped in useCallback for real-time subscriptions
  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      // Load all submissions from both sessions for current game
      const query = supabase
        .from('submissions')
        .select('*');
      
      if (currentGameId) {
        query.eq('game_id', currentGameId);
      }
      
      const { data: submissionsData, error: submissionsError } = await query.order('created_at', { ascending: false });

      if (submissionsError) {
        console.error('Error loading submissions:', submissionsError);
        setLoading(false);
        return;
      }

      if (submissionsData && submissionsData.length > 0) {
        // Load scores for each submission
        const submissionIds = submissionsData.map(s => s.id);
        const { data: scoresData, error: scoresError } = await supabase
          .from('scores')
          .select('*')
          .in('submission_id', submissionIds);

        if (scoresError) {
          console.error('Error loading scores:', scoresError);
        }

        // Combine submissions with scores
        const submissionsWithScores = submissionsData.map(submission => {
          const scores = {};
          if (scoresData) {
            scoresData
              .filter(score => score.submission_id === submission.id)
              .forEach(score => {
                scores[score.requirement_id] = score.score;
              });
          }
          return {
            id: submission.id,
            designerName: submission.designer_name,
            sessionName: submission.session_name,
            preview: submission.image_url,
            imageUrl: submission.image_url,
            image_path: submission.image_path,
            scores: scores,
            timestamp: submission.created_at
          };
        });

        console.log('[REVIEW] Loaded submissions with scores:', submissionsWithScores);
        setSubmissions(submissionsWithScores);
      } else {
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentGameId]);

  useEffect(() => {
    loadSessions();
    loadSubmissions();
    
    // Set up real-time subscription for all submissions
    const channelName = currentGameId 
      ? `review-submissions-${currentGameId}` 
      : 'review-all-submissions';
    
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'submissions' },
        (payload) => {
          console.log('Real-time submission event:', payload.eventType, payload);
          // Only reload if it's not a DELETE event (we handle DELETE manually)
          // And if it's for the current game
          if (payload.eventType !== 'DELETE') {
            if (!currentGameId || payload.new?.game_id === currentGameId || payload.old?.game_id === currentGameId) {
              loadSubmissions();
            }
          }
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'scores' },
        (payload) => {
          console.log('[REALTIME] Score changed:', payload.eventType, payload);
          console.log('[REALTIME] Submission ID:', payload.new?.submission_id || payload.old?.submission_id);
          console.log('[REALTIME] Reloading submissions to get updated scores...');
          // Reload submissions to get updated scores
          // We reload all submissions because scores are linked via submission_id,
          // and we need to check if the submission belongs to the current game
          loadSubmissions();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [currentGameId, loadSubmissions]);

  // Polling fallback for scores (in case real-time fails or is delayed)
  useEffect(() => {
    if (!currentGameId || submissions.length === 0) return;

    console.log('[REVIEW] 🔄 Setting up polling fallback for scores');

    const pollInterval = setInterval(async () => {
      try {
        // Get current submission IDs
        const submissionIds = submissions.map(s => s.id);
        if (submissionIds.length === 0) return;

        // Load current scores from database
        const { data: scoresData, error: scoresError } = await supabase
          .from('scores')
          .select('*')
          .in('submission_id', submissionIds);

        if (scoresError) {
          console.error('[REVIEW] [POLLING] Error loading scores:', scoresError);
          return;
        }

        // Build current scores map: { submissionId: { requirementId: score } }
        const currentScoresMap = {};
        if (scoresData) {
          scoresData.forEach(score => {
            if (!currentScoresMap[score.submission_id]) {
              currentScoresMap[score.submission_id] = {};
            }
            currentScoresMap[score.submission_id][score.requirement_id] = score.score;
          });
        }

        // Compare with current state to detect changes
        let hasChanges = false;
        submissions.forEach(submission => {
          const currentScores = currentScoresMap[submission.id] || {};
          const stateScores = submission.scores || {};

          // Check if any scores differ
          Object.keys(currentScores).forEach(reqId => {
            if (currentScores[reqId] !== stateScores[reqId]) {
              hasChanges = true;
            }
          });

          // Check if any scores were removed
          Object.keys(stateScores).forEach(reqId => {
            if (currentScores[reqId] === undefined && stateScores[reqId] > 0) {
              hasChanges = true;
            }
          });
        });

        if (hasChanges) {
          console.log('[REVIEW] [POLLING] Score changes detected, reloading submissions...');
          loadSubmissions();
        }
      } catch (error) {
        console.error('[REVIEW] [POLLING] Error checking scores:', error);
      }
    }, 2000); // Poll every 2 seconds as fallback

    return () => {
      console.log('[REVIEW] 🔄 Clearing polling fallback for scores');
      clearInterval(pollInterval);
    };
  }, [currentGameId, submissions, loadSubmissions]);

  // Load scoring status from games table
  const loadScoringStatus = async () => {
    if (!currentGameId) return;
    
    try {
      const { data: gameData, error } = await supabase
        .from('games')
        .select('scoring_status')
        .eq('id', currentGameId)
        .single();
      
      if (error) {
        console.error('[REVIEW] Error loading scoring status:', error);
        setScoringStatus({});
        return;
      }
      
      // scoring_status is JSONB, default to empty object if null
      setScoringStatus(gameData?.scoring_status || {});
    } catch (error) {
      console.error('[REVIEW] Error loading scoring status:', error);
      setScoringStatus({});
    }
  };

  // Save scoring status to games table
  const saveScoringStatus = async (sessionKey, isLocked) => {
    if (!currentGameId) return;
    
    try {
      const updatedStatus = {
        ...scoringStatus,
        [sessionKey]: isLocked
      };
      
      const { error } = await supabase
        .from('games')
        .update({ scoring_status: updatedStatus })
        .eq('id', currentGameId);
      
      if (error) {
        console.error('[REVIEW] Error saving scoring status:', error);
        // Revert state on error
        setScoringStatus(scoringStatus);
        return;
      }
      
      setScoringStatus(updatedStatus);
      console.log('[REVIEW] ✅ Scoring status updated:', updatedStatus);
    } catch (error) {
      console.error('[REVIEW] Error saving scoring status:', error);
      // Revert state on error
      setScoringStatus(scoringStatus);
    }
  };

  // Handle toggle change
  const handleScoringLockToggle = async (sessionKey) => {
    const currentLocked = scoringStatus[sessionKey] || false;
    await saveScoringStatus(sessionKey, !currentLocked);
  };

  // Load sessions and their requirements/client changes
  const loadSessions = async () => {
    if (!currentGameId) return;
    
    try {
      // Load scoring status
      await loadScoringStatus();
      
      // Load session briefs
      const { data: briefs, error: briefsError } = await supabase
        .from('session_briefs')
        .select('*')
        .eq('game_id', currentGameId)
        .order('session_name', { ascending: true });

      if (briefsError) {
        console.error('Error loading sessions:', briefsError);
        return;
      }

      if (briefs && briefs.length > 0) {
        const sessionsData = briefs.map(brief => ({
          sessionKey: brief.session_name,
          displayName: brief.session_display_name || (brief.session_name === 'sessionA' ? 'Session A' : brief.session_name === 'sessionB' ? 'Session B' : brief.session_name),
        }));
        setSessions(sessionsData);
        
        // Set default selected session to first session if not already set
        if (sessionsData.length > 0 && selectedSession === null) {
          setSelectedSession(sessionsData[0].sessionKey);
        }

        // Load requirements for each session
        const { data: requirements, error: reqError } = await supabase
          .from('session_requirements')
          .select('*')
          .eq('game_id', currentGameId)
          .order('created_at', { ascending: true });

        if (!reqError && requirements) {
          const reqsBySession = {};
          requirements.forEach(req => {
            if (!reqsBySession[req.session_name]) {
              reqsBySession[req.session_name] = [];
            }
            reqsBySession[req.session_name].push(req.requirement_text);
          });
          setSessionRequirements(reqsBySession);
        }

        // Load client changes for each session
        const { data: changes, error: changesError } = await supabase
          .from('client_changes')
          .select('*')
          .eq('game_id', currentGameId)
          .order('order', { ascending: true })
          .order('created_at', { ascending: true }); // Fallback to created_at if order is not set

        if (!changesError && changes) {
          const changesBySession = {};
          changes.forEach((change, index) => {
            if (!changesBySession[change.session_name]) {
              changesBySession[change.session_name] = [];
            }
            changesBySession[change.session_name].push({
              id: change.id,
              text: change.change_text,
              isBonus: change.is_bonus || false,
              points: change.points || (change.is_bonus ? 4 : 1)
            });
          });
          setClientChanges(changesBySession);
        }
      }
      
      // Load designers from multiple sources:
      // 1. From game_participants (who joined via join code)
      // 2. From submissions (who actually submitted)
      // 3. From games.designers (fallback)
      const designerSet = new Set();
      
      // Load from game_participants
      const { data: participants, error: participantsError } = await supabase
        .from('game_participants')
        .select('user_id')
        .eq('game_id', currentGameId);
      
      if (!participantsError && participants && participants.length > 0) {
        // Load user profiles for participants
        const userIds = participants.map(p => p.user_id).filter(Boolean);
        if (userIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('user_profiles')
            .select('first_name, last_name, display_name')
            .in('id', userIds);
          
          if (!profilesError && profiles) {
            profiles.forEach(profile => {
              const name = profile.display_name || 
                          `${profile.first_name || ''} ${profile.last_name || ''}`.trim() ||
                          'Unknown';
              if (name && name !== 'Unknown') {
                designerSet.add(name);
              }
            });
          }
        }
      }
      
      // Load unique designer names from submissions
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('submissions')
        .select('designer_name')
        .eq('game_id', currentGameId);
      
      if (!submissionsError && submissionsData) {
        submissionsData.forEach(sub => {
          if (sub.designer_name) {
            designerSet.add(sub.designer_name);
          }
        });
      }
      
      // Fallback: Load from games.designers if still empty
      if (designerSet.size === 0) {
        const { data: gameData, error: gameError } = await supabase
          .from('games')
          .select('designers')
          .eq('id', currentGameId)
          .single();
        
        if (!gameError && gameData?.designers) {
          const designerNames = gameData.designers.map(d => 
            typeof d === 'string' ? d : (d.name || `${d.firstName} ${d.lastName}`)
          ).filter(Boolean);
          designerNames.forEach(name => designerSet.add(name));
        }
      }
      
      // Convert Set to Array and sort
      const designerNames = Array.from(designerSet).sort();
      console.log('[REVIEW] Loaded designers:', designerNames);
      setDesigners(designerNames);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };


  const handleScoreUpdate = async (submissionId, scores) => {
    try {
      console.log('Updating scores for submission:', submissionId);
      console.log('Scores to update:', scores);
      
      const requirementIds = Object.keys(scores);
      
      for (const requirementId of requirementIds) {
        const score = scores[requirementId];
        
        console.log(`Processing requirement ${requirementId} with score ${score}`);
        
        // Check if score already exists
        const { data: existing, error: checkError } = await supabase
          .from('scores')
          .select('id')
          .eq('submission_id', submissionId)
          .eq('requirement_id', requirementId);

        if (checkError && checkError.code !== 'PGRST116') {
          console.error('Error checking for existing score:', checkError);
          throw checkError;
        }

        const existingScore = existing && existing.length > 0 ? existing[0] : null;

        if (existingScore) {
          // Update existing score
          console.log(`Updating existing score with id ${existingScore.id}`);
          const { error } = await supabase
            .from('scores')
            .update({
              score: parseInt(score) || 0,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingScore.id);

          if (error) {
            console.error('Error updating score:', error);
            throw error;
          }
          console.log('Score updated successfully');
        } else {
          // Insert new score
          console.log(`Inserting new score for requirement ${requirementId}`);
          const { error, data } = await supabase
            .from('scores')
            .insert([{
              submission_id: submissionId,
              requirement_id: requirementId,
              score: parseInt(score) || 0
            }])
            .select();

          if (error) {
            console.error('Error inserting score:', error);
            console.error('Error details:', {
              message: error.message,
              code: error.code,
              details: error.details,
              hint: error.hint
            });
            throw error;
          }
          console.log('Score inserted successfully:', data);
        }
      }

      // Update local state
      setSubmissions(submissions.map(sub => 
        sub.id === submissionId ? { ...sub, scores } : sub
      ));
      console.log('✅ Scores updated successfully');
    } catch (error) {
      console.error('Error updating score:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      alert(`Failed to update score: ${error.message || 'Please check the console for details.'}`);
    }
  };

  const handleAIAnalyze = async (submission) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const suggestions = {};
        const sessionChanges = clientChanges[submission.sessionName] || [];
        sessionChanges.forEach((req) => {
          // Use the requirement ID from the database
          if (req.id) {
            suggestions[req.id] = Math.random() > 0.5 ? (req.points || (req.isBonus ? 4 : 1)) : 0;
          }
        });
        resolve(suggestions);
      }, 1000);
    });
  };

  const handleDeleteSubmission = async (submissionId, imagePath) => {
    if (!window.confirm('Are you sure you want to delete this submission? This cannot be undone.')) {
      return;
    }

    try {
      console.log('Deleting submission:', submissionId);
      console.log('Image path:', imagePath);

      // First, remove from local state immediately to give instant feedback
      setSubmissions(submissions.filter(sub => sub.id !== submissionId));

      // Delete from storage first (if it fails, we'll still try to delete from DB)
      if (imagePath) {
        console.log('Deleting from storage:', imagePath);
        const { error: storageError } = await supabase.storage
          .from('design-submissions')
          .remove([imagePath]);

        if (storageError) {
          console.error('Error deleting from storage:', storageError);
          console.error('Storage error details:', {
            message: storageError.message,
            statusCode: storageError.statusCode
          });
          // Continue with DB deletion even if storage delete fails
        } else {
          console.log('Successfully deleted from storage');
        }
      }

      // Delete from database (this should cascade delete scores if foreign key is set up)
      console.log('Deleting from database:', submissionId);
      const { data, error: dbError } = await supabase
        .from('submissions')
        .delete()
        .eq('id', submissionId)
        .select();

      if (dbError) {
        console.error('Database deletion error:', dbError);
        console.error('Error details:', {
          message: dbError.message,
          code: dbError.code,
          details: dbError.details,
          hint: dbError.hint
        });
        throw dbError;
      }

      console.log('Database deletion result:', data);

      // Verify deletion by checking if it still exists
      const { data: verifyData, error: verifyError } = await supabase
        .from('submissions')
        .select('id')
        .eq('id', submissionId)
        .single();

      if (verifyError && verifyError.code === 'PGRST116') {
        // PGRST116 means no rows found - deletion successful
        console.log('Deletion verified - submission no longer exists');
        alert('Submission deleted successfully!');
      } else if (verifyData) {
        console.warn('Submission still exists after deletion attempt');
        // Reload to get accurate state
        await loadSubmissions();
        alert('Warning: Deletion may not have completed. Please refresh the page.');
      } else {
        console.log('Deletion appears successful');
        alert('Submission deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      
      // Reload to get accurate state
      await loadSubmissions();
      alert(`Failed to delete submission: ${error.message || 'Please check the console for details.'}`);
    }
  };


  // Filter submissions by selected designer and session
  const filteredSubmissions = selectedDesigner === 'All' 
    ? submissions 
    : submissions.filter(sub => 
        sub.designerName === selectedDesigner && sub.sessionName === selectedSession
      );

  // Get all submissions for the current session (for leaderboard - shows all designers who submitted)
  const sessionSubmissionsForLeaderboard = selectedSession 
    ? submissions.filter(sub => sub.sessionName === selectedSession)
    : submissions;

  // Reset session when designer changes
  useEffect(() => {
    if (selectedDesigner !== 'All' && sessions.length > 0) {
      // Default to first session when switching designers
      setSelectedSession(sessions[0]?.sessionKey || null);
    }
  }, [selectedDesigner, sessions]);

  // Show all designers in tabs (not just those with submissions)
  const allDesignerTabs = ['All', ...designers];

  // Get initial requirements (for reference display only)
  const getInitialRequirements = (submission) => {
    return sessionRequirements[submission.sessionName] || [];
  };

  // Get client changes (these are scored)
  const getClientChangesForSubmission = (submission) => {
    return clientChanges[submission.sessionName] || [];
  };

  // Get session display name
  const getSessionDisplayName = (sessionKey) => {
    const session = sessions.find(s => s.sessionKey === sessionKey);
    return session ? session.displayName : (sessionKey === 'sessionA' ? 'Session A' : sessionKey === 'sessionB' ? 'Session B' : sessionKey);
  };

  if (loading) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '2rem'
      }}>
        <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          <p>Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Designer Tabs - Above the card area */}
      <div style={{
        marginBottom: '2rem',
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        {allDesignerTabs.map(designer => (
          <button
            key={designer}
            onClick={() => setSelectedDesigner(designer)}
            style={{
              padding: '0.75rem 1.5rem',
              background: selectedDesigner === designer 
                ? '#D702F6' 
                : 'rgba(255, 255, 255, 0.1)',
              border: selectedDesigner === designer
                ? '2px solid #D702F6'
                : '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '0.95rem',
              fontWeight: selectedDesigner === designer ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {designer}
          </button>
        ))}
      </div>

      {/* Review & Score Card */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '2.5rem',
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h2 style={{ 
            fontSize: '1.8rem', 
            color: '#ffffff', 
            margin: 0 
          }}>
            Review & Score Submissions
          </h2>
          {selectedDesigner !== 'All' && sessions.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {sessions.map((session, index) => {
                const isLocked = scoringStatus[session.sessionKey] || false;
                return (
                  <div key={session.sessionKey} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => setSelectedSession(session.sessionKey)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: selectedSession === session.sessionKey 
                          ? '#D702F6' 
                          : 'rgba(255, 255, 255, 0.1)',
                        border: selectedSession === session.sessionKey
                          ? '2px solid #D702F6'
                          : '2px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        fontWeight: selectedSession === session.sessionKey ? 600 : 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {index + 1}. {session.displayName}
                    </button>
                    {isHost() && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        color: 'rgba(255, 255, 255, 0.7)'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          gap: '0.5rem'
                        }}>
                          <div
                            onClick={() => handleScoringLockToggle(session.sessionKey)}
                            style={{
                              position: 'relative',
                              width: '44px',
                              height: '24px',
                              borderRadius: '12px',
                              background: isLocked ? '#D702F6' : 'rgba(255, 255, 255, 0.3)',
                              transition: 'background 0.2s ease',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                top: '2px',
                                left: isLocked ? '22px' : '2px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: '#ffffff',
                                transition: 'left 0.2s ease',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                              }}
                            />
                          </div>
                          <span>{isLocked ? '🔒 Locked' : '🔓 Lock Scores'}</span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {filteredSubmissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.5)' }}>
            <p>
              {submissions.length === 0 
                ? 'No submissions yet.'
                : selectedDesigner === 'All'
                  ? 'No submissions found.'
                  : `No submissions from ${selectedDesigner} for ${getSessionDisplayName(selectedSession)}.`
              }
            </p>
          </div>
        ) : selectedDesigner === 'All' ? (
          // Show image grid for "All" tab
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1.5rem'
            }}>
              {filteredSubmissions.map(submission => (
                <div key={submission.id} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    padding: '1rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}>
                      {submission.designerName}
                    </span>
                    <span style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.85rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px'
                    }}>
                      {getSessionDisplayName(submission.sessionName)}
                    </span>
                  </div>
                  <div style={{
                    position: 'relative',
                    padding: '1rem'
                  }}>
                    <img 
                      src={submission.imageUrl || submission.preview} 
                      alt={`${submission.designerName}'s submission`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        display: 'block'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Leaderboard for All view */}
            <div style={{
              marginTop: '2rem',
              paddingTop: '2rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Leaderboard 
                submissions={submissions}
                requirements={submissions.length > 0 ? getClientChangesForSubmission(submissions[0]) : []}
              />
            </div>
          </>
        ) : (
          // Show scoring checklist for individual designer with side-by-side layout
          <>
            <div className="review-submissions-list">
              {filteredSubmissions.map(submission => {
                const isSubmissionLocked = scoringStatus[submission.sessionName] || false;
                return (
                  <div key={submission.id} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: isSubmissionLocked 
                      ? '2px solid rgba(215, 2, 246, 0.5)' 
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    opacity: isSubmissionLocked ? 0.8 : 1,
                    position: 'relative'
                  }}>
                    {isSubmissionLocked && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'rgba(215, 2, 246, 0.2)',
                        border: '1px solid rgba(215, 2, 246, 0.5)',
                        borderRadius: '6px',
                        padding: '0.25rem 0.75rem',
                        fontSize: '0.75rem',
                        color: '#D702F6',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        🔒 Scores Locked
                      </div>
                    )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <div>
                      <h3 style={{ 
                        color: '#ffffff', 
                        fontSize: '1.5rem', 
                        margin: 0,
                        marginBottom: '0.5rem'
                      }}>
                        {submission.designerName}
                      </h3>
                      <span style={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.9rem'
                      }}>
                        {getSessionDisplayName(submission.sessionName)}
                      </span>
                    </div>
                  </div>
                  <div 
                    className="review-submission-grid"
                    style={{ alignItems: 'start' }}
                  >
                    {/* Full-size image on the left */}
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: 'rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer'
                    }}
                    onClick={() => setFullscreenImage(submission.imageUrl || submission.preview)}
                    >
                      <img 
                        src={submission.imageUrl || submission.preview} 
                        alt={`${submission.designerName}'s submission`}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '8px',
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      />
                    </div>
                    {/* Requirements checklist on the right */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      border: '1px solid rgba(255, 255, 255, 0.15)'
                    }}>
                      <ScoringChecklist
                        initialRequirements={getInitialRequirements(submission)}
                        clientChanges={getClientChangesForSubmission(submission)}
                        submission={submission}
                        onScoreUpdate={handleScoreUpdate}
                        onAIAnalyze={handleAIAnalyze}
                        hideImage={true}
                        isHost={isHost()}
                        isLocked={scoringStatus[submission.sessionName] || false}
                      />
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

                    <div style={{
                      marginTop: '2rem',
                      paddingTop: '2rem',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <Leaderboard 
                        submissions={sessionSubmissionsForLeaderboard}
                        requirements={sessionSubmissionsForLeaderboard.length > 0 ? getClientChangesForSubmission(sessionSubmissionsForLeaderboard[0]) : []}
                      />
                    </div>
          </>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            cursor: 'pointer'
          }}
          onClick={() => setFullscreenImage(null)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '95vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '3rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullscreenImage}
              alt="Full size submission"
              style={{
                maxWidth: '100%',
                maxHeight: '95vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)'
              }}
            />
            <button
              onClick={() => setFullscreenImage(null)}
              style={{
                position: 'absolute',
                top: '0.5rem',
                right: '0.5rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: '#ffffff',
                fontSize: '1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                fontWeight: 'bold',
                zIndex: 10001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
              aria-label="Close fullscreen image"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ReviewPage;

